import { useDebouncedCallback } from '@/hooks/useDebounce';
import { useCallback, useState } from 'react';

/**
 * Column widths state: { columnKey: width }
 */
type ColumnWidths = Record<string, number>;

const DEBOUNCE_DELAY = 300;

const STORAGE_KEY = 'invoice_reports_column_widths:v1';

/**
 * Validate if parsed value is a valid ColumnWidths object
 */
const isColumnWidths = (value: unknown): value is ColumnWidths => {
  if (typeof value !== 'object' || value === null) return false;
  return Object.entries(value).every(
    ([key, val]) => typeof key === 'string' && typeof val === 'number'
  );
};

/**
 * Load column widths from localStorage
 * Returns empty object if not found or invalid
 */
const loadColumnWidths = (): ColumnWidths => {
  const value = localStorage.getItem(STORAGE_KEY);
  if (!value) return {};

  try {
    const parsed: unknown = JSON.parse(value);
    if (isColumnWidths(parsed)) {
      return parsed;
    }
    localStorage.removeItem(STORAGE_KEY);
    return {};
  } catch {
    localStorage.removeItem(STORAGE_KEY);
    return {};
  }
};

/**
 * Save column widths to localStorage
 */
const saveColumnWidths = (widths: ColumnWidths): void => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(widths));
};

/**
 * Hook to manage resizable column widths.
 * Persists column widths in localStorage to maintain state across searches and page changes.
 */
export const useResizableColumns = () => {
  // Lazy initializer: loadColumnWidths runs once on first render only
  const [columnWidths, setColumnWidths] =
    useState<ColumnWidths>(loadColumnWidths);

  // Debounced save to localStorage
  const debouncedSave = useDebouncedCallback(saveColumnWidths, DEBOUNCE_DELAY);

  // Get width for a specific column
  const getWidth = useCallback(
    (columnKey: string): number | null => {
      return columnWidths[columnKey] ?? null;
    },
    [columnWidths]
  );

  // Update width for a specific column (state updates immediately, localStorage save is debounced)
  const setWidth = useCallback(
    (columnKey: string, width: number) => {
      setColumnWidths((prev) => {
        const updated = { ...prev, [columnKey]: width };
        debouncedSave(updated);
        return updated;
      });
    },
    [debouncedSave]
  );

  // Handle resize event from Table component (receives columnIndex)
  const handleResize = useCallback(
    (columnIndex: number, width: number, columns: Array<{ key?: unknown }>) => {
      const column = columns[columnIndex];
      if (column?.key && typeof column.key === 'string') {
        setWidth(column.key, width);
      }
    },
    [setWidth]
  );

  return { columnWidths, getWidth, setWidth, handleResize };
};
